/**
 * Was auf der Preisseite steht – ausser den Paketen selbst.
 *
 * Die Pakete, ihre Preise und die Benutzergrenzen liegen in
 * packages/shared/src/pakete.ts: sie sind keine Seiteninhalte, sondern die
 * Grundlage, gegen die der Server eine Registrierung prüft.
 *
 * Hier bleibt, was nur diese Seite betrifft – die Vergleichstabelle und die
 * häufigen Fragen. Beides ist Text für Menschen und für nichts anderes.
 */

import { PAKETE, type Paket } from '@raporto/shared'

export type PaketId = 'starter' | 'pro' | 'team'

/**
 * Vergleich Zeile für Zeile.
 *
 * Was in allen drei Paketen gleich ist, steht bewusst mit drin und nicht nur
 * beim grössten. Ein Vergleich, der nur die Unterschiede zeigt, liest sich
 * wie eine Liste von Dingen, die im kleinen Paket fehlen.
 */
export interface VergleichsZeile {
  gruppe: string
  merkmal: string
  starter: string
  pro: string
  team: string
}

const paket = (id: PaketId): Paket => {
  const p = PAKETE.find((x) => x.id === id)
  if (!p) throw new Error(`Unbekanntes Paket: ${id}`)
  return p
}

export const VERGLEICH: VergleichsZeile[] = [
  /*
    Benutzerzahl und Speicher kommen aus den Paketen, nicht aus dieser Datei.

    Sie standen hier einmal als eigene Zeichenketten – «20», «200 GB» – neben
    denselben Angaben in packages/shared/src/pakete.ts und noch einmal auf der
    Startseite. Drei Listen, drei Zahlen: die Startseite versprach 500 GB, die
    Preisseite 200, die Paketkarte darüber ebenfalls 200, und richtig war
    keine davon. Wer eine Grenze ändert, ändert sie jetzt an einer Stelle.
  */
  {
    gruppe: 'Umfang',
    merkmal: 'Benutzer',
    starter: String(paket('starter').benutzer),
    pro: String(paket('pro').benutzer),
    team: String(paket('team').benutzer)
  },
  { gruppe: 'Umfang', merkmal: 'Betriebe pro Zugang', starter: '1', pro: '1', team: '1' },
  {
    gruppe: 'Umfang',
    merkmal: 'Speicher für Belege und Fotos',
    starter: `${paket('starter').speicherGb} GB`,
    pro: `${paket('pro').speicherGb} GB`,
    team: `${paket('team').speicherGb} GB`
  },
  {
    gruppe: 'Umfang',
    merkmal: 'Rechnungen und Offerten pro Jahr',
    starter: 'ohne Limite',
    pro: 'ohne Limite',
    team: 'ohne Limite'
  },

  { gruppe: 'Büro', merkmal: 'Rechnungen mit QR-Zahlteil', starter: 'ja', pro: 'ja', team: 'ja' },
  {
    gruppe: 'Büro',
    merkmal: 'Offerten mit eigenem Nummernkreis',
    starter: 'ja',
    pro: 'ja',
    team: 'ja'
  },
  {
    gruppe: 'Büro',
    merkmal: 'Kunden, Baustellen, Leistungskatalog',
    starter: 'ja',
    pro: 'ja',
    team: 'ja'
  },
  { gruppe: 'Büro', merkmal: 'Zahlungen und offene Posten', starter: '–', pro: 'ja', team: 'ja' },
  { gruppe: 'Büro', merkmal: 'Export für den Treuhänder', starter: '–', pro: 'ja', team: 'ja' },

  { gruppe: 'Baustelle', merkmal: 'Rapporte am Handy', starter: '–', pro: 'ja', team: 'ja' },
  { gruppe: 'Baustelle', merkmal: 'Erfassen ohne Netz', starter: '–', pro: 'ja', team: 'ja' },
  {
    gruppe: 'Baustelle',
    merkmal: 'Unterschrift auf dem Gerät',
    starter: '–',
    pro: 'ja',
    team: 'ja'
  },
  { gruppe: 'Baustelle', merkmal: 'Fotos am Rapport', starter: '–', pro: 'ja', team: 'ja' },

  {
    gruppe: 'Sicherheit',
    merkmal: 'Zwei-Faktor-Anmeldung',
    starter: 'ja',
    pro: 'ja',
    team: 'ja'
  },
  {
    gruppe: 'Sicherheit',
    merkmal: 'Getrennte Daten pro Betrieb',
    starter: 'ja',
    pro: 'ja',
    team: 'ja'
  },

  { gruppe: 'Vertrag', merkmal: 'Mindestlaufzeit', starter: 'keine', pro: 'keine', team: 'keine' },
  {
    gruppe: 'Vertrag',
    merkmal: 'Kündigung',
    starter: 'auf Monatsende',
    pro: 'auf Monatsende',
    team: 'auf Monatsende'
  },
  {
    gruppe: 'Vertrag',
    merkmal: 'Paket wechseln',
    starter: 'jederzeit',
    pro: 'jederzeit',
    team: 'jederzeit'
  },
  {
    gruppe: 'Vertrag',
    merkmal: 'Daten mitnehmen beim Aufhören',
    starter: 'ja',
    pro: 'ja',
    team: 'ja'
  }
]

/**
 * Was Leute vor dem Kauf wirklich fragen.
 *
 * Keine Frage, deren Antwort «kontaktieren Sie uns» lautet. Wenn eine Antwort
 * hier nicht steht, gehört die Frage nicht auf die Seite, sondern die Antwort
 * gefunden.
 */
export const FRAGEN: Array<{ frage: string; antwort: string }> = [
  {
    frage: 'Sind die Preise mit oder ohne Mehrwertsteuer?',
    antwort:
      'Alle Beträge verstehen sich ohne Mehrwertsteuer. Auf der Rechnung kommen 8.1 % dazu, der aktuelle Normalsatz in der Schweiz.'
  },
  {
    frage: 'Zählt jeder Monteur als Benutzer?',
    antwort:
      'Ja. Jede Person, die sich anmeldet, ist ein Benutzer – ob sie im Büro Rechnungen schreibt oder auf der Baustelle Stunden erfasst. Ein Gerät zählt nicht; wer sein Handy und den Bürorechner benutzt, ist trotzdem ein Benutzer.'
  },
  {
    frage: 'Was geschieht mit unseren Daten, wenn wir aufhören?',
    antwort:
      'Du lädst vorher den Export herunter: die abgeschlossenen Belege als PDF, dazu Kunden, Rechnungen und Zahlungen als Datei, die sich in einer Tabelle öffnen lässt. Weitere Bestände geben wir auf Anfrage heraus. Gelöscht wird auf deine Aufforderung; wir behalten nichts als Druckmittel zurück.'
  },
  {
    frage: 'Können wir Daten aus unserer bisherigen Software übernehmen?',
    antwort:
      'Kunden lassen sich als Tabelle einlesen, mit Vorschau und Erkennung von Dubletten. Abgeschlossene Rechnungen aus dem alten System werden nicht umgeschrieben – sie bleiben dort, wo sie sind, und Raporto beginnt mit einer neuen Nummernfolge.'
  },
  {
    frage: 'Läuft Raporto auch ohne Internet?',
    antwort:
      'Auf der Baustelle ja. Rapporte, Stunden, Material, Fotos und die Unterschrift entstehen auf dem Gerät und gehen hinaus, sobald wieder Netz da ist. Das Büro braucht eine Verbindung, weil dort Nummern vergeben und Belege abgeschlossen werden.'
  }
]

/* ===========================================================================
   Der Paketfinder.

   Der Vergleich beantwortet «was ist drin». Die Frage, mit der jemand auf
   diese Seite kommt, ist eine andere: «welches brauche ICH». Einundzwanzig
   Zeilen beantworten sie nicht – man liest sie, nickt und weiss es immer noch
   nicht.

   Deshalb ein paar Fragen über den eigenen Betrieb statt über die Software.
   Und zwar nur die, die wirklich etwas entscheiden: eine Frage, deren Antwort
   das Ergebnis nie ändert, ist eine Frage, die man aus Höflichkeit stellt.

   Wichtig für später: die Antwort wird NICHT hier hinterlegt, sondern aus dem
   Vergleich oben abgeleitet. Wer eine Funktion ins Starter-Paket nimmt, ändert
   eine Zeile in VERGLEICH – und der Finder sagt ab sofort dasselbe. Eine
   zweite Liste, die dieselbe Frage anders beantwortet, wäre genau der Fehler,
   wegen dem die Preise im gemeinsamen Teil liegen.
   =========================================================================== */

const SPALTEN: PaketId[] = ['starter', 'pro', 'team']

export interface FinderFrage {
  id: string
  /** Was den Besucher gefragt wird – über seinen Betrieb, nicht über uns. */
  frage: string
  /** Die Zeile im Vergleich, die diese Frage entscheidet. */
  merkmal: string
  /**
   * Der Satz, der im Ergebnis die Begründung trägt. Er steht nach «weil»,
   * deshalb kleingeschrieben und ohne Punkt.
   */
  weil: string
  /**
   * Nur bei der Benutzerfrage: die Stufen zur Auswahl. Jede trägt ihre eigene
   * Begründung – «weil so viele Leute Zugang brauchen» passt zu «Mehr als
   * fünf», aber nicht zu «Nur ich».
   */
  stufen?: Array<{ wort: string; anzahl: number; weil: string }>
}

export const FINDER: FinderFrage[] = [
  {
    id: 'benutzer',
    frage: 'Wie viele Personen melden sich an?',
    merkmal: 'Benutzer',
    weil: 'so viele Leute Zugang brauchen',
    stufen: [
      { wort: 'Nur ich', anzahl: 1, weil: 'du allein arbeitest' },
      { wort: 'Zwei bis fünf', anzahl: 5, weil: 'bis zu fünf Personen Zugang brauchen' },
      { wort: 'Mehr als fünf', anzahl: 6, weil: 'mehr als fünf Personen Zugang brauchen' }
    ]
  },
  {
    id: 'rapporte',
    frage: 'Schreiben deine Monteure Rapporte am Handy?',
    merkmal: 'Rapporte am Handy',
    weil: 'deine Monteure am Handy rapportieren'
  },
  {
    id: 'zahlungen',
    frage: 'Willst du offene Posten sehen und für den Treuhänder exportieren?',
    merkmal: 'Zahlungen und offene Posten',
    weil: 'du offene Posten und den Treuhänder-Export brauchst'
  }
]

/** Das kleinste Paket, dessen Zelle in dieser Zeile nicht «–» ist. */
export function kleinstesMit(merkmal: string): PaketId | null {
  const zeile = VERGLEICH.find((z) => z.merkmal === merkmal)
  if (!zeile) return null
  return SPALTEN.find((s) => zeile[s] !== '–') ?? null
}

/** Das kleinste Paket, das so viele Benutzer trägt. */
export function kleinstesFuerBenutzer(anzahl: number): PaketId | null {
  const zeile = VERGLEICH.find((z) => z.merkmal === 'Benutzer')
  if (!zeile) return null
  return SPALTEN.find((s) => Number(zeile[s]) >= anzahl) ?? null
}

export interface FinderErgebnis {
  paket: PaketId
  /** Die Frage, die den Ausschlag gegeben hat – für die Markierung in der Tabelle. */
  wegen: FinderFrage
  /** Der Satz nach «weil» – bei der Benutzerfrage der der gewählten Stufe. */
  weil: string
}

/**
 * Das kleinste Paket, das alle gegebenen Antworten trägt.
 *
 * Unbeantwortete Fragen zählen nicht. Wer nur die erste beantwortet, bekommt
 * ein Ergebnis für die erste – und nicht die Aufforderung, erst einmal alles
 * auszufüllen.
 */
export function finderErgebnis(
  antworten: Record<string, boolean | number | undefined>
): FinderErgebnis | null {
  let beste = -1
  let wegen: FinderFrage | null = null
  let weil = ''

  for (const frage of FINDER) {
    const antwort = antworten[frage.id]
    if (antwort === undefined) continue

    const noetig = frage.stufen
      ? kleinstesFuerBenutzer(Number(antwort))
      : antwort === true
        ? kleinstesMit(frage.merkmal)
        : null
    if (!noetig) continue

    const rang = SPALTEN.indexOf(noetig)
    if (rang > beste) {
      beste = rang
      wegen = frage
      weil = frage.stufen
        ? (frage.stufen.find((s) => s.anzahl === Number(antwort))?.weil ?? frage.weil)
        : frage.weil
    }
  }

  if (beste < 0 || !wegen) return null
  return { paket: SPALTEN[beste], wegen, weil }
}

/** Die Zeilen, in denen sich die Pakete tatsächlich unterscheiden. */
export const UNTERSCHIEDE: VergleichsZeile[] = VERGLEICH.filter(
  (z) => !(z.starter === z.pro && z.pro === z.team)
)
